import { useState } from "react";
import { motion } from "framer-motion";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { KanbanColumn } from "./KanbanColumn";
import { KanbanCard, type TicketCard } from "./KanbanCard";
import { KanbanFilters } from "./KanbanFilters";

export type ColumnType = {
  id: string;
  title: string;
  color: string;
};

const columns: ColumnType[] = [
  { id: "novo-lead", title: "Novo Lead", color: "#2563EB" },
  { id: "em-atendimento", title: "Em Atendimento", color: "#8B5CF6" },
  { id: "aguardando-cliente", title: "Aguardando Cliente", color: "#F59E0B" },
  { id: "follow-up", title: "Follow-up", color: "#06B6D4" },
  { id: "negociacao", title: "Negociação", color: "#EC4899" },
  { id: "fechado", title: "Fechado", color: "#22C55E" },
];

const initialTickets: TicketCard[] = [
  {
    id: "1",
    clientName: "Maria Santos",
    clientAvatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face",
    lastMessage: "Olá, preciso de informações sobre o plano empresarial...",
    agentName: "Carlos Silva",
    channel: "whatsapp",
    priority: "high",
    slaTime: "15min",
    slaWarning: false,
    isOnline: true,
    tags: ["VIP", "Cliente"],
    columnId: "novo-lead",
    timestamp: "Há 2 min",
  },
  {
    id: "2",
    clientName: "João Pedro Oliveira",
    clientAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
    lastMessage: "Qual o prazo para ativação do serviço?",
    agentName: "Amanda Costa",
    channel: "whatsapp",
    priority: "medium",
    slaTime: "32min",
    slaWarning: false,
    isOnline: true,
    tags: ["Lead"],
    columnId: "novo-lead",
    timestamp: "Há 5 min",
  },
  {
    id: "3",
    clientName: "Ana Julia Ferreira",
    clientAvatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
    lastMessage: "Estou aguardando retorno sobre o orçamento",
    agentName: "Bruno Mendes",
    channel: "instagram",
    priority: "low",
    slaTime: "45min",
    slaWarning: false,
    isOnline: false,
    tags: ["Cliente"],
    columnId: "em-atendimento",
    timestamp: "Há 10 min",
  },
  {
    id: "4",
    clientName: "Carlos Eduardo",
    clientAvatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face",
    lastMessage: "Preciso alterar minha forma de pagamento",
    agentName: "Julia Santos",
    channel: "whatsapp",
    priority: "high",
    slaTime: "05min",
    slaWarning: true,
    isOnline: true,
    tags: ["VIP", "Urgente"],
    columnId: "em-atendimento",
    timestamp: "Há 15 min",
  },
  {
    id: "5",
    clientName: "Fernanda Lima",
    clientAvatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face",
    lastMessage: "Vou verificar com minha equipe e retorno",
    agentName: "Rafael Oliveira",
    channel: "facebook",
    priority: "medium",
    slaTime: "1h 20min",
    slaWarning: false,
    isOnline: false,
    tags: ["Lead"],
    columnId: "aguardando-cliente",
    timestamp: "Há 25 min",
  },
  {
    id: "6",
    clientName: "Ricardo Souza",
    clientAvatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop&crop=face",
    lastMessage: "Interessado no plano premium",
    agentName: "Mariana Dias",
    channel: "widget",
    priority: "high",
    slaTime: "08min",
    slaWarning: true,
    isOnline: true,
    tags: ["VIP"],
    columnId: "aguardando-cliente",
    timestamp: "Há 30 min",
  },
  {
    id: "7",
    clientName: "Patrícia Costa",
    clientAvatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&crop=face",
    lastMessage: "Podemos agendar uma demonstração?",
    agentName: "Lucas Ferreira",
    channel: "whatsapp",
    priority: "medium",
    slaTime: "2h 15min",
    slaWarning: false,
    isOnline: true,
    tags: ["Lead", "Demo"],
    columnId: "follow-up",
    timestamp: "Há 45 min",
  },
  {
    id: "8",
    clientName: "Marcos Almeida",
    clientAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
    lastMessage: "Vamos fechar o contrato anual",
    agentName: "Beatriz Lima",
    channel: "whatsapp",
    priority: "high",
    slaTime: "12min",
    slaWarning: false,
    isOnline: true,
    tags: ["VIP", "Cliente"],
    columnId: "negociacao",
    timestamp: "Há 1h",
  },
  {
    id: "9",
    clientName: "Camila Rodrigues",
    clientAvatar: "https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=100&h=100&fit=crop&crop=face",
    lastMessage: "Contrato assinado! Obrigada!",
    agentName: "Carlos Silva",
    channel: "whatsapp",
    priority: "low",
    slaTime: "-",
    slaWarning: false,
    isOnline: false,
    tags: ["Cliente", "Fechado"],
    columnId: "fechado",
    timestamp: "Há 2h",
  },
  {
    id: "10",
    clientName: "Roberto Nascimento",
    clientAvatar: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&h=100&fit=crop&crop=face",
    lastMessage: "Excelente atendimento, parabéns!",
    agentName: "Amanda Costa",
    channel: "instagram",
    priority: "low",
    slaTime: "-",
    slaWarning: false,
    isOnline: true,
    tags: ["VIP", "Fechado"],
    columnId: "fechado",
    timestamp: "Há 3h",
  },
  {
    id: "11",
    clientName: "Luciana Martins",
    clientAvatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&crop=face",
    lastMessage: "Gostaria de saber mais sobre integrações",
    agentName: "Bruno Mendes",
    channel: "whatsapp",
    priority: "medium",
    slaTime: "28min",
    slaWarning: false,
    isOnline: true,
    tags: ["Lead"],
    columnId: "novo-lead",
    timestamp: "Há 8 min",
  },
  {
    id: "12",
    clientName: "Eduardo Santos",
    clientAvatar: "https://images.unsplash.com/photo-1463453091185-61582044d556?w=100&h=100&fit=crop&crop=face",
    lastMessage: "Preciso de suporte técnico urgente",
    agentName: "Julia Santos",
    channel: "widget",
    priority: "high",
    slaTime: "03min",
    slaWarning: true,
    isOnline: true,
    tags: ["VIP", "Urgente"],
    columnId: "em-atendimento",
    timestamp: "Há 3 min",
  },
];

export function KanbanBoard() {
  const [tickets, setTickets] = useState<TicketCard[]>(initialTickets);
  const [activeTicket, setActiveTicket] = useState<TicketCard | null>(null);
  const [showSlaWarning, setShowSlaWarning] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      ticket.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.lastMessage.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.agentName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSlaWarning = showSlaWarning ? ticket.slaWarning : true;
    return matchesSearch && matchesSlaWarning;
  });

  const getTicketsByColumn = (columnId: string) => {
    return filteredTickets.filter((ticket) => ticket.columnId === columnId);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const ticket = tickets.find((t) => t.id === active.id);
    if (ticket) {
      setActiveTicket(ticket);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeTicket = tickets.find((t) => t.id === activeId);
    const overTicket = tickets.find((t) => t.id === overId);

    if (!activeTicket) return;

    // Moving to a different column
    const overColumn = columns.find((c) => c.id === overId);
    if (overColumn) {
      setTickets((prev) =>
        prev.map((t) =>
          t.id === activeId ? { ...t, columnId: overColumn.id } : t
        )
      );
      return;
    }

    // Moving over another ticket
    if (overTicket && activeTicket.columnId !== overTicket.columnId) {
      setTickets((prev) =>
        prev.map((t) =>
          t.id === activeId ? { ...t, columnId: overTicket.columnId } : t
        )
      );
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTicket(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeId === overId) return;

    const activeTicket = tickets.find((t) => t.id === activeId);
    const overTicket = tickets.find((t) => t.id === overId);

    if (!activeTicket) return;

    // Check if dropping on a column
    const overColumn = columns.find((c) => c.id === overId);
    if (overColumn) {
      setTickets((prev) =>
        prev.map((t) =>
          t.id === activeId ? { ...t, columnId: overColumn.id } : t
        )
      );
      return;
    }

    // Reordering within the same column
    if (overTicket && activeTicket.columnId === overTicket.columnId) {
      const columnTickets = tickets.filter(
        (t) => t.columnId === activeTicket.columnId
      );
      const oldIndex = columnTickets.findIndex((t) => t.id === activeId);
      const newIndex = columnTickets.findIndex((t) => t.id === overId);

      const reorderedColumnTickets = arrayMove(
        columnTickets,
        oldIndex,
        newIndex
      );

      setTickets((prev) => {
        const otherTickets = prev.filter(
          (t) => t.columnId !== activeTicket.columnId
        );
        return [...otherTickets, ...reorderedColumnTickets];
      });
    }
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Filters Bar */}
      <KanbanFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        showSlaWarning={showSlaWarning}
        onSlaWarningChange={setShowSlaWarning}
      />

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden p-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="flex h-full gap-4">
            {columns.map((column, index) => (
              <motion.div
                key={column.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex-shrink-0"
              >
                <SortableContext
                  items={getTicketsByColumn(column.id).map((t) => t.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <KanbanColumn
                    column={column}
                    tickets={getTicketsByColumn(column.id)}
                  />
                </SortableContext>
              </motion.div>
            ))}
          </div>

          <DragOverlay>
            {activeTicket ? (
              <div className="rotate-3 opacity-90">
                <KanbanCard ticket={activeTicket} isDragging />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}